import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'wouter';
import { api, type HistoryRow, type ObjectRecord } from '../api';
import { findObjectType } from '../objectTypes';

export function RecordPage() {
  const { type, id } = useParams<{ type: string; id: string }>();
  const [, setLocation] = useLocation();
  const meta = findObjectType(type);
  const [record, setRecord] = useState<ObjectRecord | null>(null);
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRecord(null);
    setError(null);
    api
      .read(type, id)
      .then(setRecord)
      .catch((e: Error) => setError(e.message));
    api.history(type, id).then((h) => setHistory(h.results)).catch(() => undefined);
  }, [type, id]);

  if (!meta) {
    return (
      <div className="p-8">
        <h1 className="text-xl font-semibold">Unknown object type</h1>
      </div>
    );
  }

  const primary = record?.properties[meta.primary_display];

  async function save() {
    if (!record) return;
    setSaving(true);
    try {
      const updated = await api.update(type, id, editing);
      setRecord(updated);
      setEditing({});
      const h = await api.history(type, id);
      setHistory(h.results);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function archive() {
    if (!record) return;
    if (!confirm('Archive this record? It can be restored from the recycle bin.')) return;
    await api.archive(type, id);
    setLocation(`/objects/${type}`);
  }

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-4">
        <Link href={`/objects/${type}`} className="text-sm text-text-muted hover:text-accent">
          ← {meta.label_plural}
        </Link>
      </div>

      <header className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">
            {primary ? String(primary) : <span className="text-text-dim">Untitled</span>}
          </h1>
          <p className="text-text-muted text-sm font-mono">{id}</p>
          {record?.deleted_at && (
            <p className="text-warning text-sm mt-2">📦 In recycle bin</p>
          )}
        </div>
        <div className="flex gap-2">
          {Object.keys(editing).length > 0 && (
            <button onClick={save} disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
          <button onClick={archive} className="btn">
            Archive
          </button>
        </div>
      </header>

      {error && (
        <div className="card p-4 mb-4 border-danger text-danger">{error}</div>
      )}

      <div className="grid md:grid-cols-3 gap-6">
        <section className="md:col-span-2 card p-5">
          <h2 className="text-sm font-semibold mb-4 text-text-muted uppercase tracking-wider">
            Properties
          </h2>
          {record === null ? (
            <div className="text-text-dim">Loading…</div>
          ) : (
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              {meta.display_fields.map((f) => (
                <PropertyField
                  key={f}
                  field={f}
                  value={
                    editing[f] !== undefined
                      ? editing[f]
                      : record.properties[f] !== null && record.properties[f] !== undefined
                        ? String(record.properties[f])
                        : ''
                  }
                  onChange={(v) => setEditing({ ...editing, [f]: v })}
                />
              ))}
              {/* Show every property, not just display_fields. */}
              {Object.keys(record.properties)
                .filter((k) => !meta.display_fields.includes(k) && !k.startsWith('hs_'))
                .map((f) => (
                  <PropertyField
                    key={f}
                    field={f}
                    value={
                      editing[f] !== undefined
                        ? editing[f]
                        : String(record.properties[f] ?? '')
                    }
                    onChange={(v) => setEditing({ ...editing, [f]: v })}
                  />
                ))}
            </div>
          )}
        </section>

        <section className="card p-5">
          <h2 className="text-sm font-semibold mb-4 text-text-muted uppercase tracking-wider">
            Activity
          </h2>
          {history.length === 0 ? (
            <p className="text-text-dim text-sm">No history yet.</p>
          ) : (
            <ul className="space-y-3">
              {history.slice(0, 30).map((h) => (
                <li key={h.id} className="text-sm">
                  <div className="text-text-muted text-xs">
                    {new Date(h.changed_at).toLocaleString()}
                  </div>
                  <div>
                    <span className="font-medium">{prettify(h.property)}</span>{' '}
                    {h.old_value !== null && h.old_value !== undefined ? (
                      <>
                        changed from{' '}
                        <code className="text-text-muted text-xs">
                          {JSON.stringify(h.old_value)}
                        </code>{' '}
                        to{' '}
                      </>
                    ) : (
                      <>set to </>
                    )}
                    <code className="text-accent text-xs">
                      {JSON.stringify(h.new_value)}
                    </code>
                  </div>
                  <div className="text-text-dim text-xs">via {h.source}</div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function PropertyField({
  field,
  value,
  onChange,
}: {
  field: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="label">{prettify(field)}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input"
      />
    </div>
  );
}

function prettify(field: string): string {
  return field
    .replace(/^hs_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
